/**
 * Search results.
 *
 * The boundaries are keyed on the term, so each search gets a fresh pair: a
 * spinner rather than the last search's rows, and a failure that clears the
 * moment the next term is typed. Which answer belongs on screen is no longer
 * anyone's problem here — the query is keyed by the term, so a slow reply to
 * an old one lands in its own cache entry and never in the panel.
 *
 * A remote search that fails falls back to the demo crate, which is always
 * there. The reason it failed — a quota, a disabled API — arrives as a toast
 * from the query cache itself.
 */

import { useQuery } from '@tanstack/solid-query';
import { type Component, Errored, Loading, Show } from 'solid-js';
import { searchTracksQuery } from '../../app/queries.ts';
import { demoSearch } from '../../data/demo-library.ts';
import { canSearchRemote } from '../../state/library.ts';
import { searchTerm } from '../../state/ui.ts';
import { Empty, Spinner } from './SheetStates.tsx';
import { TrackList } from './TrackList.tsx';

interface TermProps {
  term: string;
}

const plural = (n: number): string => (n === 1 ? '' : 's');

const Results: Component<TermProps> = (props) => {
  const query = useQuery(() => searchTracksQuery(props.term));
  const header = (): string =>
    canSearchRemote()
      ? `${query.data.length} results`
      : `Demo crate — ${query.data.length} result${plural(query.data.length)}`;

  return (
    <Show
      when={query.data.length}
      fallback={<Empty title="No results" body={`Nothing found for “${props.term}”.`} />}
    >
      <TrackList tracks={query.data} header={header()} />
    </Show>
  );
};

/** What an empty field offers, which depends on what the app can reach. */
const Idle: Component = () => (
  <Show
    when={canSearchRemote()}
    fallback={
      <Empty
        title="Search the demo crate"
        body="Connect a Google account to search the real thing."
      />
    }
  >
    <Empty title="Search YouTube Music" body="Songs, artists, albums." />
  </Show>
);

export const SearchPanel: Component = () => (
  <Show when={searchTerm()} keyed fallback={<Idle />}>
    {(term) => (
      <Loading fallback={<Spinner />}>
        <Errored fallback={<TrackList tracks={demoSearch(term)} header="Demo crate" />}>
          <Results term={term} />
        </Errored>
      </Loading>
    )}
  </Show>
);
